import { useEffect, useState } from "react";
import { toast } from "sonner";
import { videoService } from "../api/video.api";

type AdmitPolicy = "owners" | "admins" | "participants";

interface AdmitPolicySelectProps {
  roomId: string;
  initial: AdmitPolicy;
  onChange?: (next: AdmitPolicy) => void;
}

const OPTIONS: { value: "owners" | "participants"; label: string; description: string }[] = [
  { value: "owners", label: "Only me", description: "Only you can admit waiting guests." },
  { value: "participants", label: "Anyone in the call", description: "All participants can admit waiting guests." },
];

/**
 * Owner-only toggle that sets Room.admit_policy via PATCH /rooms/<id>/.
 * "admins" (legacy) is treated the same as "owners" since there is no
 * promote-to-admin UI — both radio buttons map to the "owners" selection.
 *
 * Matches the design's own radio-circle styling exactly (weco2/call.jsx's
 * `admitPolicy` group) — not a native radio input. Indentation/dimming when
 * the waiting room is disabled is applied by the parent
 * (`InCallInviteContent`), not this component — it always renders for an
 * owner.
 */
export function AdmitPolicySelect({
  roomId,
  initial,
  onChange,
}: AdmitPolicySelectProps) {
  const [policy, setPolicy] = useState<AdmitPolicy>(initial);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setPolicy(initial);
  }, [initial]);

  // "admins" (legacy) has no dedicated radio — fold it into "owners" for
  // display so a room stuck on the legacy value still shows a selection
  // instead of leaving both circles unfilled.
  const selectedValue = policy === "admins" ? "owners" : policy;

  const handleChange = async (next: "owners" | "participants") => {
    if (busy) return;
    const prev = policy;
    setPolicy(next);
    setBusy(true);
    try {
      await videoService.updateRoom(roomId, { admit_policy: next });
      onChange?.(next);
    } catch (err) {
      console.error("AdmitPolicySelect updateRoom failed:", err);
      setPolicy(prev);
      toast.error("Could not update admit settings");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="text-[11px] font-black uppercase tracking-widest text-muted-foreground mb-2">
        Who can admit guests
      </div>
      {OPTIONS.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => void handleChange(opt.value)}
          disabled={busy}
          aria-pressed={selectedValue === opt.value}
          className="w-full flex items-start gap-3 py-2 text-start"
        >
          <span
            className={`mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
              selectedValue === opt.value ? "border-primary" : "border-border"
            }`}
          >
            {selectedValue === opt.value && <span className="w-2 h-2 rounded-full bg-primary" />}
          </span>
          <span>
            <span className="block text-sm font-bold text-foreground">{opt.label}</span>
            <span className="block text-[11px] text-muted-foreground leading-snug">{opt.description}</span>
          </span>
        </button>
      ))}
    </div>
  );
}
